import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useOKXPriceOnly } from '../hooks/useOKXPriceOnly';

// PERMANENT: Centralized pricing state management
const PricingContext = createContext();

// Pricing state reducer for predictable updates
const pricingReducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_BTC_PRICE':
      return {
        ...state,
        btcPrice: action.payload,
        lastPriceUpdate: Date.now(),
        isLive: true
      };
    
    case 'UPDATE_STRIKES':
      return {
        ...state,
        strikes: action.payload.strikes,
        selectedExpiry: action.payload.expiry,
        lastStrikeUpdate: Date.now()
      };
    
    case 'SET_EXPIRY':
      return {
        ...state,
        selectedExpiry: action.payload,
        strikeUpdateNeeded: true
      };
    
    case 'SET_CONNECTION_STATUS':
      return {
        ...state,
        isConnected: action.payload.isConnected,
        dataSource: action.payload.dataSource
      };
    
    case 'FORCE_REFRESH':
      return {
        ...state,
        forceRefresh: Date.now()
      };
    
    default:
      return state;
  }
};

// Initial state
const initialState = {
  btcPrice: 0,
  strikes: [],
  selectedExpiry: '2 HR',
  availableExpiries: ['2 HR', '4 HR', '8 HR', '12 HR'],
  isLive: false,
  isConnected: false,
  dataSource: 'Initializing...',
  lastPriceUpdate: null,
  lastStrikeUpdate: null,
  strikeUpdateNeeded: false,
  forceRefresh: 0,
  contractSize: 0.1,
  bidAskSpread: 0,
  volume24h: 0
};

export const PricingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(pricingReducer, initialState);
  const { btcPrice, isLive, dataSource, bidAskSpread } = useOKXPriceOnly();

  // PERMANENT: Price feed integration
  useEffect(() => {
    if (btcPrice && btcPrice > 0) {
      dispatch({
        type: 'UPDATE_BTC_PRICE',
        payload: btcPrice
      });
      
      dispatch({
        type: 'SET_CONNECTION_STATUS',
        payload: {
          isConnected: isLive,
          dataSource: dataSource || 'OKX Live Feed'
        }
      });
      
      console.log('🎯 PRICING CONTEXT: BTC price updated to', btcPrice);
    }
  }, [btcPrice, isLive, dataSource]);

  // PERMANENT: Strike generation with guaranteed updates
  useEffect(() => {
    if (state.btcPrice > 0 && (state.strikeUpdateNeeded || !state.strikes.length)) {
      generateStrikes(state.btcPrice, state.selectedExpiry);
    }
  }, [state.btcPrice, state.selectedExpiry, state.strikeUpdateNeeded, state.forceRefresh]);

  // PERMANENT: Professional strike generation
  const generateStrikes = (currentPrice, expiry) => {
    console.log(`🏛️ GENERATING STRIKES: ${expiry} at $${currentPrice}`);
    
    const strikeSpacing = 250;
    const atmStrike = Math.round(currentPrice / strikeSpacing) * strikeSpacing;
    
    // Time value multipliers
    const timeMultipliers = {
      '2 HR': 1.0,
      '4 HR': 1.4,
      '8 HR': 2.0,
      '1D': 3.0,
      '12 HR': 2.5
    };
    
    const multiplier = timeMultipliers[expiry] || 1.0;
    
    const strikes = [
      atmStrike - strikeSpacing,     // ITM
      atmStrike - (strikeSpacing/2), // ITM
      atmStrike,                     // ATM
      atmStrike + strikeSpacing      // OTM
    ].map(strikePrice => {
      
      // Determine moneyness
      const distanceFromATM = Math.abs(strikePrice - currentPrice);
      let callMoneyness, putMoneyness;
      
      if (strikePrice < currentPrice - 125) {
        callMoneyness = 'ITM';
        putMoneyness = 'OTM';
      } else if (strikePrice > currentPrice + 125) {
        callMoneyness = 'OTM';
        putMoneyness = 'ITM';
      } else {
        callMoneyness = 'ATM';
        putMoneyness = 'ATM';
      }
      
      // Professional premium calculation
      const calculatePremium = (optionType, moneyness) => {
        const intrinsic = optionType === 'call' 
          ? Math.max(0, currentPrice - strikePrice)
          : Math.max(0, strikePrice - currentPrice);
        
        let timeValue = 50 + (Math.random() * 80); // Base $50-130
        timeValue *= multiplier; // Scale by expiry
        
        // Adjust for moneyness
        switch(moneyness) {
          case 'ITM': timeValue *= 0.8; break;
          case 'ATM': timeValue *= 1.3; break;
          case 'OTM': timeValue *= 0.7; break;
        }
        
        return (intrinsic + timeValue) * state.contractSize;
      };
      
      const callPremium = calculatePremium('call', callMoneyness);
      const putPremium = calculatePremium('put', putMoneyness);
      
      return {
        price: strikePrice,
        call: {
          premium: parseFloat(callPremium.toFixed(2)),
          delta: callMoneyness === 'ITM' ? 0.7 : callMoneyness === 'ATM' ? 0.5 : 0.3,
          gamma: 0.02,
          theta: -0.5,
          vega: 0.15,
          rho: 0.05,
          symbol: `BTC-${strikePrice}-C-${expiry.replace(' ', '')}`,
          moneyness: callMoneyness,
          intrinsic: Math.max(0, currentPrice - strikePrice) * state.contractSize,
          time_value: callPremium - (Math.max(0, currentPrice - strikePrice) * state.contractSize)
        },
        put: {
          premium: parseFloat(putPremium.toFixed(2)),
          delta: putMoneyness === 'ITM' ? -0.7 : putMoneyness === 'ATM' ? -0.5 : -0.3,
          gamma: 0.02,
          theta: -0.5,
          vega: 0.15,
          rho: -0.05,
          symbol: `BTC-${strikePrice}-P-${expiry.replace(' ', '')}`,
          moneyness: putMoneyness,
          intrinsic: Math.max(0, strikePrice - currentPrice) * state.contractSize,
          time_value: putPremium - (Math.max(0, strikePrice - currentPrice) * state.contractSize)
        }
      };
    });
    
    dispatch({
      type: 'UPDATE_STRIKES',
      payload: {
        strikes,
        expiry
      }
    });
    
    const atmStrikeData = strikes.find(s => s.call.moneyness === 'ATM');
    console.log(`🎯 ATM STRIKE: $${atmStrikeData.price} (BTC: $${Math.round(currentPrice)})`);
    console.log(`💰 ATM PREMIUMS: Call=$${atmStrikeData.call.premium}, Put=$${atmStrikeData.put.premium}`);
    console.log(`📊 CONTRACT SIZE: ${state.contractSize} BTC (${(state.contractSize * currentPrice).toFixed(2)} USD notional)`);
  };

  // PERMANENT: Expiry change handler
  const updateStrikesForExpiry = (newExpiry) => {
    console.log(`🔄 EXPIRY CHANGE: ${state.selectedExpiry} → ${newExpiry}`);
    dispatch({
      type: 'SET_EXPIRY',
      payload: newExpiry
    });
  };

  // PERMANENT: Force refresh mechanism
  const forceRefresh = () => {
    console.log('🔄 FORCE REFRESH: Regenerating all pricing data');
    dispatch({ type: 'FORCE_REFRESH' });
  };

  // Trade execution
  const executeInstitutionalTrade = async (tradeDetails) => {
    console.log('🎯 INSTITUTIONAL TRADE:', tradeDetails);
    
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          orderId: `ORD_${Date.now()}`,
          executedAt: new Date().toISOString(),
          ...tradeDetails
        });
      }, 1000);
    });
  };

  const contextValue = {
    // State
    ...state,
    // Actions
    updateStrikesForExpiry,
    executeInstitutionalTrade,
    forceRefresh,
    // Computed values
    contractNotional: state.btcPrice * state.contractSize,
    bidAskSpread: bidAskSpread || 0.10,
    volume24h: 8730
  };

  return (
    <PricingContext.Provider value={contextValue}>
      {children}
    </PricingContext.Provider>
  );
};

export const usePricing = () => {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error('usePricing must be used within PricingProvider');
  }
  return context;
};
